import { FC, useState, ChangeEvent } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { predefinedVars } from '../AdsFilters.init';

import { styled } from '@mui/system';

import {
  RentPeriodEnum,
  TransactionTypeEnum,
  CompletionStatusEnum,
  PropertyKindEnum,
  AdFieldBedsEnum,
  AdFieldBathsEnum,
  AdFieldFurnitureEnum,
  AdFieldRenovationEnum,
  AdFieldFloorEnum,
  AdFieldOccupancyEnum,
  UserSettingsResponseType,
} from '@/types';
import { MyButton, LocationAutocomplete } from '@/components';
import { LocationModel } from '@/types/location';
import { getMoreFiltersVisibility } from '../moreFiltersRules';
import type { CreatorType, FilterState } from '../filterTypes';
import { ConditionSection } from './ConditionSection';
import { FindListingsSection } from './FindListingsSection';
import { FloorAvailabilitySection } from './FloorAvailabilitySection';
import { HandoverSection } from './HandoverSection';
import { PropertyMetricsSection } from './PropertyMetricsSection';
import { PropertyTypeSection } from './PropertyTypeSection';
import { TransactionSection } from './TransactionSection';

interface StyledDialogProps {
  width?: string;
}

interface IMyDialogTitleProps {
  dataQa?: string;
  dialogTitle: string;
  onClose?: () => void;
}

const StyledDialog = styled(Dialog, {
  shouldForwardProp: (prop) => prop !== 'width',
})(({ width }: StyledDialogProps) => ({
  '& .MuiDialog-paper': {
    width: width ? `${width}px` : '488px',
    maxWidth: '100%',
    borderRadius: '25px',
  },
}));

const StyleDialogTitle = styled(DialogTitle)(({ theme }) => ({
  fontSize: '28px',
  fontWeight: '600',
  color: '#2A3842',
  [theme.breakpoints.down('sm')]: {
    fontSize: '20px',
  },
}));

const MyDialogTitle: FC<IMyDialogTitleProps> = ({ dialogTitle, onClose, dataQa }) => {
  return (
    <StyleDialogTitle data-qa={dataQa}>
      {dialogTitle}
      {onClose ? (
        <IconButton
          aria-label='close'
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </StyleDialogTitle>
  );
};

const boxStyle = {
  display: 'flex',
  gap: '20px',
  marginTop: '12px',
  flexDirection: {
    xs: 'column',
    sm: 'row',
  },
};

const getInitialPropertyTypeTab = (propertyKinds: PropertyKindEnum[]) =>
  predefinedVars.propertyKindValuesCommercialTab.some(({ value }) => propertyKinds.includes(value))
    ? 1
    : 0;

export const MoreFiltersDialog = ({
  open,
  onClose,
  setOpenDialog,
  isRent,
  locations = [],
  inputAddressValue,
  setInputAddressValue,
  selectedLocation,
  onInputChange,
  onSelect,
  handleClear,
  filterState,
  handleChangeTransactionType,
  handleChangeRentFrequency,
  handleChangeCompletionStatus,
  handleChangePropertyKind,
  handleChangeBeds,
  handleChangeBaths,
  handleChangeFurnished,
  handleChangeRenovation,
  handleChangeFloor,
  handleChangeOccupancy,
  handleChangePostHandover,
  handleChangeHandover,
  handleChangePrice,
  handleChangeSize,
  handleChangeDownPayment,
  handleChangeKeywords,
  handleChangeCreator,
  handleShowResult,
  handleResetFilters,
  handleChangeMyAgency,
  handleChangeMyFriend,
  isTariffFree,
  userSettings,
}: {
  open: boolean;
  onClose: () => void;
  setOpenDialog: (value: boolean) => void;
  isRent?: boolean;
  locations: LocationModel[];
  inputAddressValue: string;
  setInputAddressValue: (value: string) => void;
  selectedLocation?: LocationModel | null;
  onInputChange?: (value: string) => void;
  onSelect?: (value: LocationModel, isQueryParams?: boolean) => void;
  handleClear?: () => void;
  filterState: FilterState;
  handleChangeTransactionType: (value: TransactionTypeEnum, name?: string) => void;
  handleChangeRentFrequency: (value: RentPeriodEnum, name?: string) => void;
  handleChangeCompletionStatus: (value: CompletionStatusEnum, name?: string) => void;
  handleChangePropertyKind: (value: PropertyKindEnum, name?: string) => void;
  handleChangeBeds: (value: AdFieldBedsEnum, name?: string) => void;
  handleChangeBaths: (value: AdFieldBathsEnum, name?: string) => void;
  handleChangeFurnished: (value: AdFieldFurnitureEnum | string, name?: string) => void;
  handleChangeRenovation: (value: AdFieldRenovationEnum | string, name?: string) => void;
  handleChangeFloor: (valie: AdFieldFloorEnum | string, name?: string) => void;
  handleChangeOccupancy: (value: AdFieldOccupancyEnum, name?: string) => void;
  handleChangePostHandover: (value: boolean, name?: string) => void;
  handleChangeHandover: (value: string, name?: string) => void;
  handleChangePrice: (
    e: ChangeEvent<HTMLInputElement>,
    name?: string,
    isQueryParams?: boolean
  ) => void;
  handleChangeSize: (
    e: ChangeEvent<HTMLInputElement>,
    name?: string,
    isQueryParams?: boolean
  ) => void;
  handleChangeDownPayment: (value: number) => void;
  handleChangeKeywords: (e: ChangeEvent<HTMLInputElement>) => void;
  handleChangeCreator: (value: CreatorType, name?: string) => void;
  handleShowResult: () => void;
  handleResetFilters: () => void;
  handleChangeMyAgency: (value: boolean) => void;
  handleChangeMyFriend: (value: boolean) => void;
  isTariffFree: boolean;
  userSettings: UserSettingsResponseType | null;
}) => {
  const [propertyTypeTab, setPropertyTypeTab] = useState<number>(() =>
    getInitialPropertyTypeTab(filterState.propertyKind)
  );

  const rentSalesData = isRent
    ? predefinedVars.rentFrequencyValues
    : predefinedVars.transactionTypeValues;
  const rentSaleName = isRent ? 'rentFrequency' : 'transactionType';
  const rentSaleTitle = isRent ? 'Rent period' : 'Transaction type';
  const rentSaleDataQa = isRent ? 'rent_frequency' : 'transaction_type';

  const {
    isDistress,
    handoverVisible,
    bedsAndBathsVisible: bedsAndBathsIsVisible,
    furnishedVisible,
    floorVisible,
    renovationVisible,
    availabilityVisible,
  } = getMoreFiltersVisibility(filterState, isRent);
  const quarterActive = filterState.multiHandover.length === 1 ? false : true;

  return (
    <StyledDialog open={open} onClose={onClose} width='900' data-qa={'more_filters_dialog'}>
      <MyDialogTitle onClose={onClose} dialogTitle='More filters' dataQa='more_filters_title' />

      <DialogContent
        sx={{
          overflowY: 'auto',
          textAlign: 'center',
          padding: '20px 30px',
        }}
        dividers
        data-qa={'more_filters_content'}
      >
        <Grid container alignItems='flex-start' justifyContent='space-between' spacing={3}>
          <Grid item xs={12}>
            <LocationAutocomplete
              setInputAddressValue={setInputAddressValue}
              locations={locations}
              selectedLocation={selectedLocation}
              inputAddressValue={inputAddressValue}
              onInputChange={onInputChange}
              handleClear={handleClear}
              onSelect={onSelect}
            />
          </Grid>
          <TransactionSection<string>
            activeValue={isRent ? filterState.rentFrequency : filterState.transactionType}
            completionStatus={filterState.completionStatus}
            completionStatusOptions={predefinedVars.completionStatusValues}
            completionStatusVisible={isDistress}
            dataQa={rentSaleDataQa}
            name={rentSaleName}
            onCompletionStatusChange={handleChangeCompletionStatus}
            onTransactionChange={isRent ? handleChangeRentFrequency : handleChangeTransactionType}
            options={rentSalesData}
            title={rentSaleTitle}
          />
          <PropertyTypeSection
            onPropertyKindChange={handleChangePropertyKind}
            onPropertyTypeTabChange={setPropertyTypeTab}
            options={
              propertyTypeTab === 0
                ? predefinedVars.propertyKindValuesResidentialTab
                : predefinedVars.propertyKindValuesCommercialTab
            }
            propertyTypeTab={propertyTypeTab}
            selectedPropertyKinds={filterState.propertyKind}
          />
          <PropertyMetricsSection
            baths={filterState.baths}
            bathsOptions={predefinedVars.bathsValues}
            beds={filterState.beds}
            bedsAndBathsVisible={bedsAndBathsIsVisible}
            bedsOptions={predefinedVars.bedsValues}
            downPayment={filterState.downPayment}
            downPaymentVisible={handoverVisible}
            onBathsChange={handleChangeBaths}
            onBedsChange={handleChangeBeds}
            onDownPaymentChange={handleChangeDownPayment}
            onPriceChange={handleChangePrice}
            onSizeChange={handleChangeSize}
            price={filterState.price}
            priceTitle={`Price (${userSettings?.currency})`}
            size={filterState.size}
            sizeSubTitle={[
              `Minimum size (${userSettings?.areaUnits.toLowerCase()})`,
              `Maximum size (${userSettings?.areaUnits.toLowerCase()})`,
            ]}
            sizeTitle={`Size (${userSettings?.areaUnits.toLowerCase()})`}
          />
          <ConditionSection
            furnished={filterState.furniture}
            furnishedOptions={predefinedVars.furnitureValues}
            furnishedVisible={furnishedVisible}
            onFurnishedChange={handleChangeFurnished}
            onRenovationChange={handleChangeRenovation}
            renovation={filterState.renovation}
            renovationOptions={predefinedVars.renovationValues}
            renovationVisible={renovationVisible}
          />
          <HandoverSection
            activeQuarter={filterState.handover.quarter}
            activeYears={filterState.multiHandover}
            disabled={quarterActive}
            handoverOptions={predefinedVars.handover}
            onHandoverChange={handleChangeHandover}
            onPostHandoverChange={handleChangePostHandover}
            postHandover={filterState.postHandover}
            postHandoverOptions={predefinedVars.postHandoverValues}
            visible={handoverVisible}
          />
          <FloorAvailabilitySection
            availability={filterState.occupancy}
            availabilityOptions={predefinedVars.availabilityValues}
            availabilityVisible={availabilityVisible}
            floor={filterState.floor}
            floorOptions={predefinedVars.floorValues}
            floorVisible={floorVisible}
            onAvailabilityChange={handleChangeOccupancy}
            onFloorChange={handleChangeFloor}
          />
          <FindListingsSection
            creator={filterState.creator}
            isMyAgency={filterState.isMyAgency}
            isTariffFree={isTariffFree}
            keywords={filterState.keywords}
            myFriends={filterState.myFriends}
            onCreatorChange={handleChangeCreator}
            onKeywordsChange={handleChangeKeywords}
            onMyAgencyChange={handleChangeMyAgency}
            onMyFriendsChange={handleChangeMyFriend}
          />
        </Grid>
      </DialogContent>

      <DialogActions
        style={{ justifyContent: 'left', margin: '0 0 15px 15px' }}
        data-qa={'more_filters_action'}
      >
        <Box sx={boxStyle}>
          <MyButton
            data={{
              customWidth: '226px',
              buttonName: 'Show results',
              variant: 'contained',
              buttonType: 'submit',
              dataQa: 'show_results_btn',
            }}
            onClick={() => {
              handleShowResult();
              setOpenDialog(false);
            }}
          />
          <MyButton
            data={{
              customWidth: '226px',
              buttonName: 'Reset',
              variant: 'outlined',
              styleType: 'cancel',
              dataQa: 'reset_btn',
            }}
            onClick={() => {
              handleResetFilters();
              setOpenDialog(false);
            }}
          />
        </Box>
      </DialogActions>
    </StyledDialog>
  );
};
